import { CommonBasesType } from 'components/SearchModal/types'

import { Currency, CurrencyAmount, Percent } from '@pancakeswap/sdk'
import {
  AutoColumn,
  Button,
  RowBetween,
  Text,
  AutoRow,
  Box,
  useModal,
  Message,
  MessageText,
  PreTitle,
  DynamicSection,
  Flex,
  IconButton,
} from '@pancakeswap/uikit'
import {
  LiquidityChartRangeInput,
  ZOOM_LEVELS,
  ZoomLevels,
  ConfirmationModalContent,
  ENumericalInput,
} from '@pancakeswap/widgets-internal'

import { logGTMClickAddLiquidityEvent } from 'utils/customGTMEventTracking'
import { tryParsePrice } from 'hooks/v3/utils'

import useV3DerivedInfo from 'hooks/v3/useV3DerivedInfo'
import { FeeAmount, NonfungiblePositionManager } from '@pancakeswap/v3-sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import CurrencyInputPanel from 'components/ECurrencyInputPanel'
import { useUserSlippage, useIsExpertMode } from '@pancakeswap/utils/user'

import { maxAmountSpend } from 'utils/maxAmountSpend'
import { basisPointsToPercent } from 'utils/exchange'
import { Field } from 'state/mint/actions'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'

import { useTransactionAdder } from 'state/transactions/hooks'
import { useV3NFTPositionManagerContract } from 'hooks/useContract'
import { useRouter } from 'next/router'
import { useIsTransactionUnsupported, useIsTransactionWarning } from 'hooks/Trades'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from '@pancakeswap/localization'
import { useSendTransaction, useWalletClient } from 'wagmi'
import { styled } from 'styled-components'
import TransactionConfirmationModal from 'components/TransactionConfirmationModal'
import EBox from 'components/EBox'

import EButtonSm from 'components/EButtonSm'
import { Bound } from 'config/constants/types'
import { V3SubmitButton } from 'views/AddLiquidityV3/components/V3SubmitButton'
import { formatCurrencyAmount, formatRawAmount } from 'utils/formatCurrencyAmount'
import { QUICK_ACTION_CONFIGS } from 'views/AddLiquidityV3/types'
import { isUserRejected } from 'utils/sentry'
import { hexToBigInt } from 'viem'
import { getViemClients } from 'utils/viem'
import { calculateGasMargin } from 'utils'
import SettingsModal from 'components/EMenu/GlobalSettings/SettingsModal'
import { SettingsMode } from 'components/EMenu/GlobalSettings/types'
import { formatDecimals } from '@pancakeswap/utils/formatBalance'
import { CurrencyLogo } from 'components/Logo'
import { useDensityChartData } from 'views/AddLiquidityV3/hooks/useDensityChartData'
import RangeSelector from './components/RangeSelector'
import { PositionPreview } from './components/PositionPreview'
import RateToggle from './components/RateToggle'
import LockedDeposit from './components/LockedDeposit'
import { useRangeHopCallbacks } from './form/hooks/useRangeHopCallbacks'
import { useV3MintActionHandlers } from './form/hooks/useV3MintActionHandlers'
import { useV3FormAddLiquidityCallback, useV3FormState } from './form/reducer'
import { useInitialRange } from './form/hooks/useInitialRange'


const StyledInput = styled(ENumericalInput)`
  background-color: ${({ theme }) => theme.colors.input};
  box-shadow: ${({ theme, error }) => theme.shadows[error ? 'warning' : 'inset']};
  width: 100%;
`

export const HideMedium = styled.div`
  ${({ theme }) => theme.mediaQueries.md} {
    display: none;
  }
`

export const MediumOnly = styled.div`
  display: none;
  ${({ theme }) => theme.mediaQueries.md} {
    display: initial;
  }
`

export const RightContainer = styled(AutoColumn)`
  height: fit-content;

  grid-row: 2 / 3;
  grid-column: 1;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-row: 1 / 3;
    grid-column: 2;
  }
`

const ButtonQuick = styled(EButtonSm)`
`

interface V3FormViewPropsType {
  feeAmount?: FeeAmount
  baseCurrency: Currency | undefined
  quoteCurrency: Currency | undefined
  invertCurrency: boolean
  currencyIdA: string | undefined
  currencyIdB: string | undefined
  liquidityUSD: number
  currentPrice: number
  token0Percentage: number
  token1Percentage: number
}

const SettingsButton = styled(IconButton)`
  display: flex;
  gap: 4px;
  background: rgba(250, 250, 250, 0.08);
  padding: 8px 16px;
  height: auto;
  width: auto;
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.textSubtle};
  font-size: 12px;
`

const GrayBox = styled(Box)`
  background: ${({ theme }) => theme.colors.input};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  padding: 24px;
  border-radius: 12px;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 16px;
  }
`

const RangeTag = styled(Text)<{ inRange: boolean }>`
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  background: ${({ theme, inRange }) => inRange ? theme.colors.success : theme.colors.failure};
  color: ${({ theme }) => theme.colors.backgroundAlt};
`

const InfoItem = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    width: auto;
    margin-bottom: 0;
    align-items: center;
  }
`

const InfoContainer = styled(Flex)`
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center; 
  }
`

const PriceRangeText = styled(Text)`
  font-family: inherit;
  font-size: 16px;
  word-break: break-word;
  
  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 18px;
  }
`

export default function V3FormView({
  feeAmount,
  baseCurrency: currencyA,
  quoteCurrency: currencyB,
  invertCurrency,
  currencyIdA,
  currencyIdB,
  liquidityUSD,
  currentPrice,
  token0Percentage,
  token1Percentage,
}: V3FormViewPropsType) {
  const router = useRouter()
  const [ inverted, setInvert ] = useState(invertCurrency)
  const { data: signer } = useWalletClient()
  const { sendTransactionAsync } = useSendTransaction()
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm

  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()
  const expertMode = useIsExpertMode()

  const positionManager = useV3NFTPositionManagerContract()
  const { account, chainId, isWrongNetwork } = useActiveWeb3React()
  const addTransaction = useTransactionAdder()
  
  const baseCurrency = useMemo(() => {
    return inverted ? currencyB : currencyA
  }, [currencyA, currencyB, inverted])

  const quoteCurrency = useMemo(() => {
    return inverted ? currencyA : currencyB
  }, [currencyA, currencyB, inverted])

  // mint state
  const formState = useV3FormState()
  const { independentField, typedValue, startPriceTypedValue, leftRangeTypedValue, rightRangeTypedValue } = formState

  const {
    pool,
    ticks,
    dependentField,
    price,
    pricesAtTicks,
    parsedAmounts,
    currencyBalances,
    position,
    noLiquidity,
    currencies,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit,
  } = useV3DerivedInfo(
    currencyA ?? undefined,
    currencyB ?? undefined,
    feeAmount,
    baseCurrency ?? undefined,
    undefined,
    formState,
  )

  useEffect(() => setInvert(invertCurrency), [invertCurrency])

  const { onFieldAInput, onFieldBInput, onLeftRangeInput, onRightRangeInput, onStartPriceInput, onBothRangeInput } =
    useV3MintActionHandlers(noLiquidity)

  const onBothRangePriceInput = useCallback(
    (leftRangeValue: string, rightRangeValue: string) => {
      const adjustedCurrentPrice = price ? parseFloat((invertPrice ? price.invert() : price).toSignificant(8)) : undefined
      if (adjustedCurrentPrice) {
        onBothRangeInput({
          leftTypedValue: tryParsePrice(
            baseCurrency?.wrapped,
            quoteCurrency?.wrapped,
            (
              adjustedCurrentPrice * ZOOM_LEVELS[feeAmount ?? FeeAmount.MEDIUM].initialMin
            ).toString(),
          ),
          rightTypedValue: tryParsePrice(
            baseCurrency?.wrapped,
            quoteCurrency?.wrapped,
            (
              adjustedCurrentPrice * ZOOM_LEVELS[feeAmount ?? FeeAmount.MEDIUM].initialMax
            ).toString(),
          ),
        })
      }
    },
    [price, feeAmount, invertPrice, onBothRangeInput, baseCurrency, quoteCurrency],
  )

  const onLeftRangePriceInput = useCallback(
    (leftRangeValue: string) => {
      onLeftRangeInput(tryParsePrice(baseCurrency?.wrapped, quoteCurrency?.wrapped, leftRangeValue))
    },
    [baseCurrency, quoteCurrency, onLeftRangeInput],
  )

  const onRightRangePriceInput = useCallback(
    (rightRangeValue: string) => {
      onRightRangeInput(tryParsePrice(baseCurrency?.wrapped, quoteCurrency.wrapped, rightRangeValue))
    },
    [baseCurrency, quoteCurrency, onRightRangeInput],
  )

  const isValid = !errorMessage && !invalidRange

  // modal and loading
  // capital efficiency warning
  const [showCapitalEfficiencyWarning, setShowCapitalEfficiencyWarning] = useState<boolean>(false)

  useEffect(() => {
    setShowCapitalEfficiencyWarning(false)
  }, [baseCurrency, quoteCurrency, feeAmount])

  useEffect(() => {
    if (feeAmount) {
      setActiveQuickAction(undefined)
      onBothRangeInput({
        leftTypedValue: undefined,
        rightTypedValue: undefined,
      })
    }
    // NOTE: ignore exhaustive-deps to avoid infinite re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeAmount])

  const onAddLiquidityCallback = useV3FormAddLiquidityCallback()

  // txn values
  const deadline = useTransactionDeadline() // custom from users settings
  const [txHash, setTxHash] = useState<string>('')
  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  //   // get the max amounts user can add
  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = useMemo(
    () =>
      [Field.CURRENCY_A, Field.CURRENCY_B].reduce((accumulator, field) => {
        return {
          ...accumulator,
          [field]: maxAmountSpend(currencyBalances[field]),
        }
      }, {}),
    [currencyBalances],
  )

  const nftPositionManagerAddress = useV3NFTPositionManagerContract()?.address
  // check whether the user has approved the router on the tokens
  const {
    approvalState: approvalA,
    approveCallback: approveACallback,
    revokeCallback: revokeACallback,
    currentAllowance: currentAllowanceA,
  } = useApproveCallback(parsedAmounts[Field.CURRENCY_A], nftPositionManagerAddress)
  const {
    approvalState: approvalB,
    approveCallback: approveBCallback,
    revokeCallback: revokeBCallback,
    currentAllowance: currentAllowanceB,
  } = useApproveCallback(parsedAmounts[Field.CURRENCY_B], nftPositionManagerAddress)

  const [allowedSlippage] = useUserSlippage() // custom from users

  const onAdd = useCallback(async () => {
    if (!chainId || !signer || !account || !nftPositionManagerAddress) return

    if (!positionManager || !baseCurrency || !quoteCurrency) {
      return
    }

    if (position && account && deadline) {
      const useNative = baseCurrency.isNative ? baseCurrency : quoteCurrency.isNative ? quoteCurrency : undefined

      const { calldata, value } = NonfungiblePositionManager.addCallParameters(position, {
        slippageTolerance: basisPointsToPercent(allowedSlippage),
        recipient: account,
        deadline: deadline.toString(),
        useNative,
        createPool: noLiquidity,
      })

      setAttemptingTxn(true)
      const txn = {
        data: calldata,
        to: nftPositionManagerAddress,
        value: hexToBigInt(value),
        account,
      }
      getViemClients({ chainId })
        .estimateGas(txn)
        .then((gas) => {
          sendTransactionAsync({
            ...txn,
            gas: calculateGasMargin(gas),
          })
            .then((response) => {
              const baseAmount = formatRawAmount(
                parsedAmounts[Field.CURRENCY_A]?.quotient?.toString() ?? '0',
                baseCurrency.decimals,
                4,
              )
              const quoteAmount = formatRawAmount(
                parsedAmounts[Field.CURRENCY_B]?.quotient?.toString() ?? '0',
                quoteCurrency.decimals,
                4,
              )

              setAttemptingTxn(false)
              addTransaction(response, {
                type: 'add-liquidity-v3',
                summary: `Add ${baseAmount} ${baseCurrency?.symbol} and ${quoteAmount} ${quoteCurrency?.symbol}`,
              })
              setTxHash(response.hash)
              onAddLiquidityCallback(response.hash)
            })
            .catch((error) => {
              console.error('Failed to send transaction', error)
              setAttemptingTxn(false)
              // we only care if the error is something _other_ than the user rejected the tx
              if (!isUserRejected(error)) {
                console.error(error)
              }
            })
        })
    }
  }, [
    account,
    addTransaction,
    allowedSlippage,
    baseCurrency,
    chainId,
    deadline,
    nftPositionManagerAddress,
    noLiquidity,
    onAddLiquidityCallback,
    parsedAmounts,
    position,
    positionManager,
    quoteCurrency,
    sendTransactionAsync,
    signer,
  ])

  const handleDismissConfirmation = useCallback(() => {
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
    }
    setTxHash('')
  }, [onFieldAInput, txHash])
  const addIsUnsupported = useIsTransactionUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  // get value and prices at ticks
  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks
  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } = pricesAtTicks

  useInitialRange(baseCurrency?.wrapped, quoteCurrency?.wrapped)

  const { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper, getSetFullRange } =
    useRangeHopCallbacks(baseCurrency ?? undefined, quoteCurrency ?? undefined, feeAmount, tickLower, tickUpper, pool)
  // we need an existence check on parsed amounts for single-asset deposits
  const showApprovalA = approvalA !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_A]
  const showApprovalB = approvalB !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_B]

  const translationData = useMemo(
    () => ({
      amountA: !depositADisabled ? formatCurrencyAmount(parsedAmounts[Field.CURRENCY_A], 4, locale) : '',
      symbolA: !depositADisabled && currencies[Field.CURRENCY_A]?.symbol ? currencies[Field.CURRENCY_A].symbol : '',
      amountB: !depositBDisabled ? formatCurrencyAmount(parsedAmounts[Field.CURRENCY_B], 4, locale) : '',
      symbolB: !depositBDisabled && currencies[Field.CURRENCY_B]?.symbol ? currencies[Field.CURRENCY_B].symbol : '',
    }),
    [depositADisabled, depositBDisabled, parsedAmounts, locale, currencies],
  )

  const pendingText = useMemo(
    () =>
      !outOfRange
        ? t('Supplying %amountA% %symbolA% and %amountB% %symbolB%', translationData)
        : t('Supplying %amountA% %symbolA% %amountB% %symbolB%', translationData),
    [t, outOfRange, translationData],
  )

  const [activeQuickAction, setActiveQuickAction] = useState<number>()
  const isQuickButtonUsed = useRef(false)

  const [onPresentAddLiquidityModal] = useModal(
    <TransactionConfirmationModal
      minWidth={['100%', null, '420px']}
      title={t('Add Liquidity')}
      customOnDismiss={handleDismissConfirmation}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      content={() => (
        <ConfirmationModalContent
          topContent={() =>
            position ? (
              <PositionPreview
                position={position}
                inRange={!outOfRange}
                ticksAtLimit={ticksAtLimit}
                baseCurrencyDefault={baseCurrency}
              />
            ) : null
          }
          bottomContent={() => (
            <Button width="100%" mt="16px" onClick={onAdd}>
              {t('Add')}
            </Button>
          )}
        />
      )}
      pendingText={pendingText}
    />,
    true,
    true,
    'TransactionConfirmationModal',
  )

  const addIsWarning = useIsTransactionWarning(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const handleButtonSubmit = useCallback(() => {
    // eslint-disable-next-line no-unused-expressions
    expertMode ? onAdd() : onPresentAddLiquidityModal()
    logGTMClickAddLiquidityEvent()
  }, [expertMode, onAdd, onPresentAddLiquidityModal])

  const buttons = (
    <V3SubmitButton
      addIsUnsupported={addIsUnsupported}
      addIsWarning={addIsWarning}
      account={account ?? undefined}
      isWrongNetwork={Boolean(isWrongNetwork)}
      approvalA={approvalA}
      approvalB={approvalB}
      isValid={isValid}
      showApprovalA={showApprovalA}
      approveACallback={approveACallback}
      currentAllowanceA={currentAllowanceA}
      revokeACallback={revokeACallback}
      currencies={currencies}
      showApprovalB={showApprovalB}
      approveBCallback={approveBCallback}
      currentAllowanceB={currentAllowanceB}
      revokeBCallback={revokeBCallback}
      parsedAmounts={parsedAmounts}
      onClick={handleButtonSubmit}
      attemptingTxn={attemptingTxn}
      errorMessage={errorMessage}
      buttonText={t('Add')}
      depositADisabled={depositADisabled}
      depositBDisabled={depositBDisabled}
    />
  )

  useEffect(() => {
    if (!isQuickButtonUsed.current && activeQuickAction) {
      setActiveQuickAction(undefined)
    } else if (isQuickButtonUsed.current) {
      isQuickButtonUsed.current = false
    }
  }, [isQuickButtonUsed, activeQuickAction, leftRangeTypedValue, rightRangeTypedValue])

  const handleRefresh = useCallback(
    (zoomLevel?: ZoomLevels) => {
      setActiveQuickAction(undefined)
      const adjustedCurrentPrice = price ? parseFloat((invertPrice ? price.invert() : price).toSignificant(8)) : undefined
      if (adjustedCurrentPrice) {
        onBothRangeInput({
          leftTypedValue: tryParsePrice(
            baseCurrency.wrapped,
            quoteCurrency.wrapped,
            (
              adjustedCurrentPrice * ZOOM_LEVELS[feeAmount ?? FeeAmount.MEDIUM].initialMin
            ).toString(),
          ),
          rightTypedValue: tryParsePrice(
            baseCurrency.wrapped,
            quoteCurrency.wrapped,
            (
              adjustedCurrentPrice * ZOOM_LEVELS[feeAmount ?? FeeAmount.MEDIUM].initialMax
            ).toString(),
          ),
        })
      }
    },
    [price, feeAmount, invertPrice, onBothRangeInput, baseCurrency, quoteCurrency],
  )

  const {
    isLoading: isChartDataLoading,
    error: chartDataError,
    formattedData,
  } = useDensityChartData({
    currencyA: baseCurrency ?? undefined,
    currencyB: quoteCurrency ?? undefined,
    feeAmount,
  })

  useEffect(() => handleRefresh(), [inverted, handleRefresh])

  const [onPresentSettingsModal] = useModal(<SettingsModal mode={SettingsMode.SWAP_LIQUIDITY} />)

  return (
    <>
      <Text fontSize="20px" mb="16px">{t("Current Position")}</Text>
      <GrayBox mb="24px">
        <Flex alignItems="flex-start" mb="16px" flexDirection="column">
          <PriceRangeText mr="8px" mb="4px">
            {priceLower?.toSignificant(6)} - {priceUpper?.toSignificant(6)}
          </PriceRangeText>
          <RangeTag inRange={!outOfRange}>
            {outOfRange ? t("Out of Range") : t("In Range")}
          </RangeTag>
        </Flex>
        <InfoContainer>
          <InfoItem>
            <Text color="textSubtle" fontSize="14px" mb="4px" textAlign="center">{t("Liquidity")}</Text>
            <Text fontSize="14px">${formatDecimals(liquidityUSD ?? '0', 2)}</Text>
          </InfoItem>
          <InfoItem>
            <Text color="textSubtle" fontSize="14px" mb="4px" textAlign="center">{t("Current Price")}</Text>
            <Text fontSize="14px">{formatDecimals(currentPrice)}</Text>
          </InfoItem>
          <InfoItem>
            <Text color="textSubtle" fontSize="14px" mb="4px" textAlign="center">{t("Deposit Ratio")}</Text>
            <Flex alignItems="center" justifyContent="center">
              <Text fontSize="14px" mr="8px">
                {t("%percent0%%/%percent1%%", {percent0: token0Percentage ? token0Percentage.toFixed(2) : 0, percent1: token1Percentage ? token1Percentage.toFixed(2): 0})}
              </Text>
              <CurrencyLogo currency={baseCurrency} size="16px" />
              <CurrencyLogo currency={quoteCurrency} size="16px" style={{ marginLeft: '4px' }} />
            </Flex>
          </InfoItem>
        </InfoContainer>
      </GrayBox>

      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Text fontSize="16px">{t("Add Liquidity")}</Text>
        <SettingsButton scale="sm" variant="tertiary" onClick={onPresentSettingsModal}>
          <img src="/assets/edit.png" style={{ height: '12px' }} alt="" />
          {t("%slippage%%", { slippage: allowedSlippage / 100 })}
        </SettingsButton>
      </Flex>

      <DynamicSection
        style={{
          gridAutoRows: 'max-content',
          gridAutoColumns: '100%',
          gap: '8px'
        }}
        disabled={invalidPool}
      >
        <Box mb="8px">
          <LockedDeposit locked={depositADisabled}>
            <CurrencyInputPanel
              showUSDPrice
              maxAmount={maxAmounts[Field.CURRENCY_A]}
              onMax={() => onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')}
              onPercentInput={(percent) =>
                onFieldAInput(maxAmounts[Field.CURRENCY_A]?.multiply(new Percent(percent, 100))?.toExact() ?? '')
              }
              disableCurrencySelect
              value={formattedAmounts[Field.CURRENCY_A] ?? '0'}
              onUserInput={onFieldAInput}
              showQuickInputButtons={[50]}
              showMaxButton
              currency={currencies[Field.CURRENCY_A]}
              id="add-liquidity-input-tokena"
              showCommonBases
              commonBasesType={CommonBasesType.LIQUIDITY}
            />
          </LockedDeposit>
        </Box>
        <LockedDeposit locked={depositBDisabled}>
          <CurrencyInputPanel
            showUSDPrice
            maxAmount={maxAmounts[Field.CURRENCY_B]}
            onMax={() => onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')}
            onPercentInput={(percent) =>
              onFieldBInput(maxAmounts[Field.CURRENCY_B]?.multiply(new Percent(percent, 100))?.toExact() ?? '')
            }
            disableCurrencySelect
            value={formattedAmounts[Field.CURRENCY_B] ?? '0'}
            onUserInput={onFieldBInput}
            showQuickInputButtons={[50]}
            showMaxButton
            currency={currencies[Field.CURRENCY_B]}
            id="add-liquidity-input-tokenb"
            showCommonBases
            commonBasesType={CommonBasesType.LIQUIDITY}
          />
        </LockedDeposit>
      </DynamicSection>
      <HideMedium>{buttons}</HideMedium>

      <RightContainer>
        <AutoColumn gap="16px">
          {price && baseCurrency && quoteCurrency && (
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mt="5px">
                <Text>{t('Current Price')}</Text>
                <RateToggle currencyA={baseCurrency} handleRateToggle={() => setInvert(!inverted)}/>
              </Flex>
              <Text fontWeight={500} fontSize={12} color="text1" textAlign="center">
                {`1 ${baseCurrency.symbol} = ${price.toSignificant(6)} ${quoteCurrency.symbol}`}
              </Text>
            </Box>
          )}
          
          <MediumOnly>{buttons}</MediumOnly>
        </AutoColumn>
      </RightContainer>
    </>
  )
}
